import type { AccessSnapshot, AuthSession, DataGateway, PlatformSupportSession } from '@/data/types';
import type { Company, Membership, MembershipProfile, Profile } from '@/domain/types';
import type { PermissionKey } from '@/domain/permissions';

const ACTIVE_COMPANY_PREFIX = 'taskflow.activeCompany.';

export interface SessionSnapshot {
  session: AuthSession;
  profile: Profile | null;
  membershipProfile: MembershipProfile | null;
  memberships: Membership[];
  companies: Company[];
  activeCompanyId: string | null;
  access: AccessSnapshot | null;
  isPlatformOwner: boolean;
  platformSupportSession: PlatformSupportSession | null;
}

function readStoredCompany(userId: string): string | null {
  try {
    return localStorage.getItem(`${ACTIVE_COMPANY_PREFIX}${userId}`);
  } catch {
    return null;
  }
}

function storeCompany(userId: string, companyId: string | null): void {
  try {
    const key = `${ACTIVE_COMPANY_PREFIX}${userId}`;
    if (companyId) localStorage.setItem(key, companyId);
    else localStorage.removeItem(key);
  } catch {
    // تجاهل في البيئات بلا تخزين محلي
  }
}

export async function loadSessionSnapshot(gateway: DataGateway): Promise<SessionSnapshot | null> {
  // إعادة بناء سياق السياسة أولًا: يضمن ألا تُقرأ الجداول بسياق قديم/فارغ
  // بعد تسجيل دخول أو خروج أو تغيير عضوية (وضع محلي).
  if (gateway.refresh) await gateway.refresh();

  const session = await gateway.auth.getSession();
  if (!session) return null;

  const profile = await gateway.table('profiles').get(session.userId).catch(() => null);
  const memberships = await gateway.table('memberships').list({
    filter: { userId: { op: 'eq', value: session.userId }, status: { op: 'eq', value: 'active' } },
    orderBy: [{ field: 'createdAt', direction: 'asc' }],
  });
  const companies = await gateway.table('companies').list({
    orderBy: [{ field: 'name', direction: 'asc' }],
  });

  const supportSession = gateway.getPlatformSupportSession
    ? await gateway.getPlatformSupportSession().catch(() => null)
    : null;
  const stored = readStoredCompany(session.userId);
  const active = supportSession
    ? memberships.find((membership) => membership.companyId === supportSession.companyId) ?? null
    : memberships.find((membership) => membership.companyId === stored) ?? memberships[0] ?? null;
  const activeCompanyId = supportSession?.companyId ?? active?.companyId ?? null;
  if (gateway.setActiveCompany) await gateway.setActiveCompany(activeCompanyId);

  const membershipProfile = active && !supportSession
    ? await gateway.table('membership_profiles').get(active.id).catch(() => null)
    : null;

  const access = gateway.getAccessSnapshot
    ? await gateway.getAccessSnapshot(activeCompanyId).catch(() => null)
    : null;

  return {
    session,
    profile,
    membershipProfile,
    memberships,
    companies,
    activeCompanyId,
    access,
    isPlatformOwner: access?.isPlatformOwner ?? false,
    platformSupportSession: supportSession,
  };
}

export async function switchCompany(
  gateway: DataGateway,
  userId: string,
  companyId: string,
  memberships: Membership[],
): Promise<SessionSnapshot | null> {
  const allowed = memberships.some((membership) => membership.companyId === companyId);
  if (!allowed) throw new Error('ليست لديك عضوية في هذه المنشأة');
  storeCompany(userId, companyId);
  if (gateway.setActiveCompany) await gateway.setActiveCompany(companyId);
  return loadSessionSnapshot(gateway);
}

export async function signOut(gateway: DataGateway): Promise<void> {
  await gateway.auth.signOut();
  // لا نمسح البيانات المحلية عند الخروج العادي: قد تكون هناك تغييرات
  // بانتظار المزامنة. المسح متاح صراحة من إعدادات الجهاز مع تأكيد.
}

export function hasPermission(access: AccessSnapshot | null, permission: string): boolean {
  return access?.permissions.includes(permission as PermissionKey) ?? false;
}

export function hasAnyPermission(access: AccessSnapshot | null, permissions: string[]): boolean {
  return permissions.some((permission) => hasPermission(access, permission));
}
